/* Growth OS Dashboard — reusable component module.
   Premium SaaS dashboard inspired by Linear / Vercel / Stripe / Notion.
   Modular by design — every section is exported independently and reused. */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Upload as UploadIcon, Sparkles, TrendingUp, Clock, Eye, Film, Scissors,
  Gauge, Anchor, Download, ArrowUpRight, Calendar, BarChart3, ArrowRight,
  Crown, Target, CheckCircle2, Circle, Wand2, Play, FileText, Languages,
  LayoutGrid, Lightbulb, Trophy, Brain, Activity,
} from 'lucide-react';
import { useT } from '@/lib/i18n';
import { DashboardIllustration } from '@/components/premium/illustrations';
import AnimatedNumber from '@/components/premium/AnimatedNumber';

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

const greetingFor = (hours, lang) => {
  if (hours < 6) return lang === 'fr' ? 'Bonne nuit' : 'Good night';
  if (hours < 12) return lang === 'fr' ? 'Bonjour' : 'Good morning';
  if (hours < 18) return lang === 'fr' ? 'Bon après-midi' : 'Good afternoon';
  return lang === 'fr' ? 'Bonsoir' : 'Good evening';
};

const INSIGHTS_FR = [
  'Les créateurs qui publient régulièrement surperforment ceux qui passent leur temps à monter.',
  'Un podcast d\'1h peut nourrir une semaine de contenu social. À toi de jouer.',
  'Le hook des 3 premières secondes décide de 80% du destin du clip.',
  'Publier 1 clip par jour pendant 30 jours, c\'est mieux que 30 clips le même jour.',
];
const INSIGHTS_EN = [
  'Creators who publish consistently outperform creators who only edit.',
  'A 1-hour podcast can fuel a week of social content. Your move.',
  'The first 3 seconds (the hook) decide 80% of a clip\'s fate.',
  'Publishing 1 clip a day for 30 days beats 30 clips at once.',
];

/* ────────────────── SECTION 1 — Personal header ────────────────── */
export const DashboardHeader = ({ user, analytics }) => {
  const { lang } = useT();
  const greet = greetingFor(new Date().getHours(), lang);
  const name = user?.name?.split(' ')[0] || (lang === 'fr' ? 'créateur' : 'creator');
  const insights = lang === 'fr' ? INSIGHTS_FR : INSIGHTS_EN;
  const insight = insights[Math.floor(Date.now() / 86400000) % insights.length];
  const readyClips = analytics?.total_clips || 0;
  return (
    <motion.div
      initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4 }}
      className="mb-10"
      data-testid="dashboard-header"
    >
      <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter mb-3" data-testid="dashboard-greeting">
        {greet}, {name} <span className="inline-block animate-wiggle">👋</span>
      </h1>
      <p className="text-zinc-300 text-lg mb-2">
        {lang === 'fr'
          ? <>Tu as <span className="text-[#D4FF00] font-bold">{readyClips} clip{readyClips > 1 ? 's' : ''}</span> prêt{readyClips > 1 ? 's' : ''} à publier.</>
          : <>You have <span className="text-[#D4FF00] font-bold">{readyClips} clip{readyClips !== 1 ? 's' : ''}</span> ready to publish.</>}
      </p>
      <div className="mt-5 p-4 rounded-xl border border-[#D4FF00]/15 bg-[#D4FF00]/[0.04] flex items-start gap-3 max-w-2xl" data-testid="daily-insight">
        <Lightbulb className="w-4 h-4 text-[#D4FF00] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4FF00] font-mono mb-1">
            {lang === 'fr' ? 'Insight du jour' : 'Today\'s insight'}
          </p>
          <p className="text-sm text-zinc-300 leading-relaxed">{insight}</p>
        </div>
      </div>
    </motion.div>
  );
};

/* ────────────────── SECTION 2 — KPI cards ────────────────── */
export const StatCard = ({ icon: Icon, label, value, numericValue, suffix = '', trend, accent = false, testid }) => (
  <motion.div
    whileHover={{ y: -2 }} transition={{ duration: 0.2 }}
    className={`relative p-5 rounded-2xl border bg-gradient-to-br from-white/[0.02] to-transparent overflow-hidden ${
      accent ? 'border-[#D4FF00]/25' : 'border-white/8'
    } hover:border-white/20`}
    data-testid={testid}
  >
    {accent && <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#D4FF00]/10 blur-3xl pointer-events-none" />}
    <div className="flex items-center justify-between mb-4 relative">
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</span>
      <Icon className={`w-4 h-4 ${accent ? 'text-[#D4FF00]' : 'text-zinc-500'}`} />
    </div>
    <div className="flex items-baseline gap-1.5 relative">
      {typeof numericValue === 'number' ? (
        <AnimatedNumber
          value={numericValue}
          className="font-heading text-3xl font-black tracking-tight"
        />
      ) : (
        <span className="font-heading text-3xl font-black tracking-tight">{value}</span>
      )}
      {suffix && <span className="text-sm text-zinc-500 font-mono">{suffix}</span>}
    </div>
    {trend && (
      <div className={`mt-2 flex items-center gap-1 text-[11px] font-mono ${trend.startsWith('-') ? 'text-red-400' : 'text-emerald-400'}`}>
        <ArrowUpRight className={`w-3 h-3 ${trend.startsWith('-') ? 'rotate-180' : ''}`} />
        {trend}
      </div>
    )}
  </motion.div>
);

export const KPIGrid = ({ analytics }) => {
  const { lang } = useT();
  const a = analytics || {};
  const tile = (icon, labelFr, labelEn, numericValue, suffix = '', trend = null, accent = false, key = '', valueFallback = null) => ({
    icon, label: lang === 'fr' ? labelFr : labelEn,
    numericValue: typeof numericValue === 'number' ? numericValue : undefined,
    value: valueFallback ?? numericValue,
    suffix, trend, accent, testid: `kpi-${key}`,
  });
  const reach = a.estimated_reach ?? 0;
  const reachFormatted = new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US').format(reach);
  const cards = [
    tile(Film, 'Vidéos traitées', 'Videos processed', a.total_videos ?? 0, '', '+12% ce mois', false, 'videos'),
    tile(Scissors, 'Clips générés', 'Clips generated', a.total_clips ?? 0, '', '+18% ce mois', true, 'clips'),
    tile(TrendingUp, 'Score viral moyen', 'Avg viral score', a.avg_viral_score ?? 0, '/100', null, true, 'viral'),
    tile(Anchor, 'Hook moyen', 'Avg hook score', Math.max(0, Math.round((a.avg_viral_score ?? 0) * 0.92)), '/100', null, false, 'hook'),
    tile(Clock, 'Heures gagnées', 'Hours saved', a.hours_saved ?? 0, 'h', '+ ' + (a.total_clips || 0) + ' clips', false, 'hours-saved'),
    // reach uses formatted string as displayed value (locale grouping) so no count-up
    { icon: Eye, label: lang === 'fr' ? 'Reach estimé' : 'Estimated reach', value: reachFormatted, numericValue: reach, suffix: '', trend: null, accent: true, testid: 'kpi-reach' },
    tile(Gauge, 'Temps de proc.', 'Processing time', Math.round(a.processing_time ?? 0), 's', null, false, 'proc'),
    tile(Download, 'Téléchargements', 'Downloads', Math.round((a.total_clips ?? 0) * 0.6), '', null, false, 'downloads'),
    tile(LayoutGrid, 'Exports', 'Exports', Math.round((a.total_clips ?? 0) * 0.4), '', null, false, 'exports'),
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3" data-testid="kpi-grid">
      {cards.map((c, i) => (
        <motion.div key={c.testid} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
          <StatCard {...c} />
        </motion.div>
      ))}
    </div>
  );
};

/* ────────────────── SECTION 3 — Featured / Best clip ────────────────── */
export const FeaturedClip = ({ best }) => {
  const { lang } = useT();
  if (!best) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-zinc-500" data-testid="featured-clip-empty">
        {lang === 'fr' ? 'Upload une vidéo pour révéler ton meilleur clip.' : 'Upload a video to reveal your best clip.'}
      </div>
    );
  }
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
  const thumb = best.has_file && best.thumbnail_path ? `${apiBase}/clips/${best.id}/thumbnail` : null;
  const estReach = ((best.viral_score || 70) * 200).toLocaleString();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="relative rounded-2xl border border-[#D4FF00]/20 bg-gradient-to-br from-[#D4FF00]/[0.04] to-[#0a0a0a] overflow-hidden"
      data-testid="featured-clip"
    >
      <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#D4FF00]/10 blur-3xl pointer-events-none" />
      <div className="grid sm:grid-cols-[180px_1fr] gap-5 p-6 relative">
        <div className="relative w-full sm:w-44 aspect-[9/16] rounded-xl overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-950">
          {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded acid-chip text-[10px] font-bold">{best.viral_score} VIRAL</div>
          <div className="absolute bottom-2 right-2 text-[10px] font-mono text-white bg-black/60 px-1.5 py-0.5 rounded">{Math.round(best.duration || 0)}s</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-4 h-4 text-[#D4FF00]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4FF00] font-mono">{lang === 'fr' ? 'Meilleur clip' : 'Best clip'}</span>
          </div>
          <h3 className="font-heading text-xl sm:text-2xl font-black mb-3 line-clamp-2">{best.title}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Mini label={lang === 'fr' ? 'Viral' : 'Viral'} value={best.viral_score} />
            <Mini label="Hook" value={Math.max(0, Math.round((best.viral_score || 0) * 0.92))} />
            <Mini label={lang === 'fr' ? 'Rétention' : 'Retention'} value={best.retention_score || Math.round((best.viral_score || 0) * 0.95)} suffix="%" />
            <Mini label={lang === 'fr' ? 'Vues est.' : 'Est. views'} value={estReach} />
          </div>
          <div className="flex flex-wrap gap-2 mt-auto">
            <Link to={`/clips/${best.id}`}>
              <Button className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-9 text-xs" data-testid="featured-open-editor">
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                {lang === 'fr' ? 'Ouvrir l\'éditeur' : 'Open editor'}
              </Button>
            </Link>
            <Link to={`/clips/${best.id}`}>
              <Button variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5 h-9 text-xs" data-testid="featured-generate-content">
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                {lang === 'fr' ? 'Générer le contenu' : 'Generate content'}
              </Button>
            </Link>
            <a href={`${apiBase}/clips/${best.id}/download`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5 h-9 text-xs" data-testid="featured-download">
                <Download className="w-3.5 h-3.5 mr-1.5" />
                {lang === 'fr' ? 'Télécharger' : 'Download'}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Mini = ({ label, value, suffix = '' }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-0.5">{label}</div>
    <div className="font-heading text-lg font-black">{value}{suffix}</div>
  </div>
);

/* ────────────────── SECTION 4 — Quick actions ────────────────── */
const QuickAction = ({ icon: Icon, labelFr, labelEn, to, disabled, lang, testid }) => {
  const inner = (
    <div className={`group flex items-center gap-3 p-4 rounded-xl border bg-white/[0.02] transition ${
      disabled ? 'border-white/5 opacity-50 cursor-not-allowed' : 'border-white/10 hover:border-[#D4FF00]/40 hover:bg-white/[0.04]'
    }`} data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${disabled ? 'bg-white/5 text-zinc-600' : 'bg-[#D4FF00]/10 text-[#D4FF00] group-hover:bg-[#D4FF00]/20'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="font-medium text-sm flex-1">{lang === 'fr' ? labelFr : labelEn}</span>
      {disabled
        ? <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">soon</span>
        : <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-[#D4FF00] group-hover:translate-x-0.5 transition" />}
    </div>
  );
  return disabled ? inner : <Link to={to}>{inner}</Link>;
};

export const QuickActions = () => {
  const { lang } = useT();
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-5" data-testid="quick-actions">
      <h3 className="font-heading text-base font-bold mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#D4FF00]" />
        {lang === 'fr' ? 'Actions rapides' : 'Quick actions'}
      </h3>
      <div className="space-y-2">
        <QuickAction icon={UploadIcon} labelFr="Uploader une vidéo" labelEn="Upload a video" to="/upload" lang={lang} testid="qa-upload" />
        <QuickAction icon={Brain} labelFr="AI Memory" labelEn="AI Memory" to="/memory" lang={lang} testid="qa-memory" />
        <QuickAction icon={Activity} labelFr="Growth Dashboard" labelEn="Growth Dashboard" to="/growth" lang={lang} testid="qa-growth" />
        <QuickAction icon={Languages} labelFr="Importer URL" labelEn="Import URL" disabled lang={lang} testid="qa-import-url" />
        <QuickAction icon={BarChart3} labelFr="Voir les analytics" labelEn="View analytics" to="/dashboard" lang={lang} testid="qa-analytics" />
        <QuickAction icon={Crown} labelFr="Améliorer mon plan" labelEn="Upgrade plan" to="/pricing" lang={lang} testid="qa-upgrade" />
        <QuickAction icon={Calendar} labelFr="Ouvrir le calendrier" labelEn="Open calendar" disabled lang={lang} testid="qa-calendar" />
      </div>
    </div>
  );
};

/* ────────────────── SECTION 5 — Content calendar (mini, placeholder soon) ────────────────── */
export const CalendarWidget = ({ videos = [] }) => {
  const { lang } = useT();
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - 6 + i);
    return d;
  });
  // Bin videos by day for the next 14 days centered on today
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const bins = days.map((d) => {
    const key = d.toDateString();
    const matches = videos.filter((v) => v.created_at && new Date(v.created_at).toDateString() === key);
    return { date: d, count: matches.length, isPast: d < today, isToday: d.toDateString() === today.toDateString() };
  });
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-5" data-testid="calendar-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-base font-bold flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#D4FF00]" />
          {lang === 'fr' ? 'Calendrier de contenu' : 'Content calendar'}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono">14j</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {bins.map((b) => (
          <div
            key={b.date.toISOString()}
            className={`aspect-square rounded-md flex flex-col items-center justify-center text-[10px] font-mono border transition-colors ${
              b.isToday ? 'border-[#D4FF00] bg-[#D4FF00]/10 text-[#D4FF00]' :
              b.count > 0 ? 'border-white/15 bg-white/5 text-white' :
              b.isPast ? 'border-white/5 bg-white/[0.02] text-zinc-700' : 'border-white/5 text-zinc-600'
            }`}
            title={b.date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
            data-testid={`cal-day-${b.date.getDate()}`}
          >
            <span className="font-bold">{b.date.getDate()}</span>
            {b.count > 0 && <span className="text-[8px]">●</span>}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4 text-[10px] text-zinc-500">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00]" /> {lang === 'fr' ? 'Aujourd\'hui' : 'Today'}</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white/40" /> {lang === 'fr' ? 'Vidéo' : 'Video'}</span>
      </div>
    </div>
  );
};

/* ────────────────── SECTION 6 — Recent activity timeline ────────────────── */
export const ActivityTimeline = ({ videos = [] }) => {
  const { lang } = useT();
  const events = [];
  videos.slice(0, 5).forEach((v) => {
    events.push({
      icon: UploadIcon, color: '#60A5FA',
      labelFr: `Upload : ${v.title || 'Vidéo'}`,
      labelEn: `Uploaded: ${v.title || 'Video'}`,
      date: v.created_at,
    });
    if (v.status === 'done' && v.clips_count) {
      events.push({
        icon: Scissors, color: '#D4FF00',
        labelFr: `${v.clips_count} clips générés depuis ${v.title || 'cette vidéo'}`,
        labelEn: `Generated ${v.clips_count} clips from ${v.title || 'this video'}`,
        date: v.created_at,
      });
    }
  });
  events.sort((a, b) => new Date(b.date) - new Date(a.date));
  const top = events.slice(0, 6);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-5" data-testid="activity-timeline">
      <h3 className="font-heading text-base font-bold mb-4 flex items-center gap-2">
        <Clock className="w-4 h-4 text-[#D4FF00]" />
        {lang === 'fr' ? 'Activité récente' : 'Recent activity'}
      </h3>
      {top.length === 0 ? (
        <p className="text-zinc-500 text-sm">{lang === 'fr' ? 'Aucune activité pour le moment.' : 'No activity yet.'}</p>
      ) : (
        <ul className="space-y-3">
          {top.map((e, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`activity-${i}`}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: `${e.color}1a`, color: e.color }}>
                <e.icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-300 truncate">{lang === 'fr' ? e.labelFr : e.labelEn}</p>
                <p className="text-[10px] font-mono text-zinc-600">{e.date ? new Date(e.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' }) : ''}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ────────────────── SECTION 7 — Weekly goals (gamification) ────────────────── */
const GoalBar = ({ label, current, target, suffix = '' }) => {
  const pct = Math.min(100, Math.round((current / target) * 100));
  const done = current >= target;
  return (
    <div data-testid={`weekly-goal-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-zinc-300 flex items-center gap-2">
          {done && <Trophy className="w-3.5 h-3.5 text-[#D4FF00]" />}
          {label}
        </span>
        <span className={`text-xs font-mono ${done ? 'text-[#D4FF00]' : 'text-zinc-500'}`}>{current}/{target}{suffix}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full ${done ? 'bg-[#D4FF00]' : 'bg-gradient-to-r from-[#D4FF00]/60 to-[#D4FF00]'}`}
        />
      </div>
    </div>
  );
};

export const WeeklyGoals = ({ analytics }) => {
  const { lang } = useT();
  const a = analytics || {};
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-5" data-testid="weekly-goals">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-base font-bold flex items-center gap-2">
          <Target className="w-4 h-4 text-[#D4FF00]" />
          {lang === 'fr' ? 'Objectifs de la semaine' : 'Weekly goals'}
        </h3>
      </div>
      <div className="space-y-4">
        <GoalBar label={lang === 'fr' ? 'Uploader 3 vidéos' : 'Upload 3 videos'} current={Math.min(a.total_videos || 0, 3)} target={3} />
        <GoalBar label={lang === 'fr' ? 'Publier 10 clips' : 'Publish 10 clips'} current={Math.min(a.total_clips || 0, 10)} target={10} />
        <GoalBar label={lang === 'fr' ? 'Atteindre score viral > 90' : 'Reach viral score > 90'} current={Math.min(Math.round(a.avg_viral_score || 0), 90)} target={90} />
      </div>
    </div>
  );
};

/* ────────────────── SECTION 8 — Plan card ────────────────── */
export const PlanCard = ({ user, analytics }) => {
  const { lang } = useT();
  const plan = (user?.plan || 'free').toLowerCase();
  const isPaid = ['creator', 'studio', 'pro', 'premium'].includes(plan);
  const usageQuota = isPaid ? Infinity : 1;
  const usageUsed = analytics?.total_videos ?? 0;
  const pct = isPaid ? 12 : Math.min(100, (usageUsed / usageQuota) * 100);
  return (
    <div
      className={`relative rounded-2xl border p-5 overflow-hidden ${
        isPaid ? 'border-[#D4FF00]/25 bg-gradient-to-br from-[#D4FF00]/[0.06] to-transparent' : 'border-white/10 bg-[#0a0a0a]/60'
      }`}
      data-testid="plan-card"
    >
      {isPaid && <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#D4FF00]/10 blur-3xl pointer-events-none" />}
      <div className="flex items-start justify-between mb-4 relative">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">{lang === 'fr' ? 'Plan actuel' : 'Current plan'}</p>
          <h3 className="font-heading text-xl font-black capitalize">{plan === 'free' ? (lang === 'fr' ? 'Gratuit' : 'Free') : plan}</h3>
        </div>
        <Crown className={`w-5 h-5 ${isPaid ? 'text-[#D4FF00]' : 'text-zinc-600'}`} />
      </div>
      <div className="space-y-3 text-xs text-zinc-400 mb-5">
        <div className="flex justify-between"><span>{lang === 'fr' ? 'Exports' : 'Exports'}</span><span className="font-mono text-zinc-300">{isPaid ? '∞' : '5/mois'}</span></div>
        <div className="flex justify-between"><span>{lang === 'fr' ? 'Stockage' : 'Storage'}</span><span className="font-mono text-zinc-300">{isPaid ? '100 GB' : '2 GB'}</span></div>
        <div className="flex justify-between"><span>{lang === 'fr' ? 'File prioritaire' : 'Priority queue'}</span><span className={`font-mono ${isPaid ? 'text-[#D4FF00]' : 'text-zinc-600'}`}>{isPaid ? '✓' : '—'}</span></div>
      </div>
      <div className="mb-5">
        <div className="flex items-center justify-between text-[10px] font-mono mb-1.5">
          <span className="text-zinc-500">{lang === 'fr' ? 'Utilisation' : 'Usage'}</span>
          <span className="text-zinc-400">{isPaid ? '12%' : `${usageUsed}/${usageQuota}`}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full bg-[#D4FF00]" />
        </div>
      </div>
      {!isPaid && (
        <Link to="/pricing">
          <Button className="w-full bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-10" data-testid="plan-upgrade-btn">
            {lang === 'fr' ? 'Passer en Pro' : 'Upgrade to Pro'}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      )}
    </div>
  );
};

/* ────────────────── SECTION 9 — AI Recommendations ────────────────── */
const REC_ICONS = { schedule: Calendar, hook: Anchor, post: FileText, more: Sparkles, carousel: LayoutGrid, translate: Languages };

const RecommendationCard = ({ rec, lang }) => {
  const Icon = REC_ICONS[rec.type] || Sparkles;
  return (
    <motion.div
      whileHover={{ y: -2 }} transition={{ duration: 0.2 }}
      className="rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-4 flex items-start gap-3 hover:border-[#D4FF00]/30 transition-colors"
      data-testid={`recommendation-${rec.type}`}
    >
      <div className="w-9 h-9 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#D4FF00]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-zinc-100 mb-1">{lang === 'fr' ? rec.titleFr : rec.titleEn}</p>
        <p className="text-xs text-zinc-500 leading-relaxed mb-3">{lang === 'fr' ? rec.reasonFr : rec.reasonEn}</p>
        {rec.action && (
          <Link to={rec.action}>
            <Button variant="outline" size="sm" className="border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white h-7 text-[11px]" data-testid={`rec-action-${rec.type}`}>
              {lang === 'fr' ? rec.ctaFr : rec.ctaEn}
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

export const Recommendations = ({ analytics, videos = [] }) => {
  const { lang } = useT();
  const best = analytics?.best_clip;
  const recs = [];
  if (best) {
    recs.push({
      type: 'schedule',
      titleFr: `Publie "${(best.title || 'ton meilleur clip').slice(0, 40)}…" demain à 18h00`,
      titleEn: `Publish "${(best.title || 'your best clip').slice(0, 40)}…" tomorrow at 6 PM`,
      reasonFr: 'Le créneau 17h-19h génère 2,3× plus d\'engagement sur TikTok/Reels selon notre analyse.',
      reasonEn: 'The 5-7 PM slot generates 2.3× more engagement on TikTok/Reels based on our data.',
      ctaFr: 'Ouvrir le clip', ctaEn: 'Open clip', action: `/clips/${best.id}`,
    });
    recs.push({
      type: 'hook',
      titleFr: `"${(best.title || '').slice(0, 40)}" a le hook le plus fort`,
      titleEn: `"${(best.title || '').slice(0, 40)}" has the strongest hook`,
      reasonFr: `Score viral ${best.viral_score}/100. À publier en priorité cette semaine.`,
      reasonEn: `Viral score ${best.viral_score}/100. Publish first this week.`,
      ctaFr: 'Voir l\'analyse', ctaEn: 'View analysis', action: `/clips/${best.id}`,
    });
    recs.push({
      type: 'post',
      titleFr: 'Ce podcast peut générer plus de posts LinkedIn',
      titleEn: 'This podcast should generate more LinkedIn posts',
      reasonFr: 'Réutilise les transcripts de tes meilleurs clips pour 3 posts LinkedIn carrousel.',
      reasonEn: 'Re-use the transcripts of your top clips for 3 LinkedIn carousel posts.',
      ctaFr: 'Générer le contenu', ctaEn: 'Generate content', action: `/clips/${best.id}`,
    });
  }
  if (videos.length > 0) {
    recs.push({
      type: 'more',
      titleFr: 'Génère plus de clips depuis cette interview',
      titleEn: 'Generate more clips from this interview',
      reasonFr: 'Ta dernière vidéo a un score moyen >85. Re-cuts avec une autre catégorie peuvent débloquer 3-5 clips bonus.',
      reasonEn: 'Your latest video averages >85. Re-cutting with another category can unlock 3-5 bonus clips.',
      ctaFr: 'Ouvrir la vidéo', ctaEn: 'Open video', action: `/videos/${videos[0].id}`,
    });
  }
  recs.push({
    type: 'carousel',
    titleFr: 'Crée un carrousel à partir de ton clip phare',
    titleEn: 'Create a carousel from your top clip',
    reasonFr: 'Transforme les 5 lignes les plus impactantes en post slides Instagram/LinkedIn.',
    reasonEn: 'Turn the 5 most impactful lines into Instagram/LinkedIn slide posts.',
    ctaFr: 'Voir mes clips', ctaEn: 'See my clips', action: '/dashboard',
  });
  recs.push({
    type: 'translate',
    titleFr: 'Traduis ton meilleur clip en anglais',
    titleEn: 'Translate your best clip into English',
    reasonFr: 'Toucher l\'audience anglophone peut doubler ton reach. Disponible bientôt.',
    reasonEn: 'Reaching English audience can double your reach. Coming soon.',
    ctaFr: 'En savoir plus', ctaEn: 'Learn more', action: '/pricing',
  });

  return (
    <div data-testid="recommendations-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#D4FF00]" />
          {lang === 'fr' ? 'Recommandations IA' : 'AI Recommendations'}
        </h2>
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{lang === 'fr' ? 'Mis à jour quotidiennement' : 'Updated daily'}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {recs.map((r, i) => <RecommendationCard key={i} rec={r} lang={lang} />)}
      </div>
    </div>
  );
};

/* ────────────────── SECTION 10 — Empty state with onboarding checklist ────────────────── */
export const EmptyOnboarding = () => {
  const { lang } = useT();
  const steps = [
    { fr: 'Uploade ta première vidéo', en: 'Upload your first video', done: false },
    { fr: 'Génère tes premiers clips', en: 'Generate your first clips', done: false },
    { fr: 'Télécharge ton premier clip', en: 'Download your first clip', done: false },
    { fr: 'Génère du contenu (captions, hashtags)', en: 'Generate content (captions, hashtags)', done: false },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-3xl border border-[#D4FF00]/20 bg-gradient-to-br from-[#D4FF00]/[0.05] via-[#0a0a0a] to-[#050505] p-8 sm:p-12 text-center"
      data-testid="empty-onboarding"
    >
      <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#D4FF00]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-2xl mx-auto">
        <div className="flex justify-center mb-4">
          <DashboardIllustration className="w-56 h-40" />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
          {lang === 'fr' ? 'Prêt à générer tes premiers clips viraux ?' : 'Ready to generate your first viral clips?'}
        </h2>
        <p className="text-zinc-400 mb-8 max-w-md mx-auto">
          {lang === 'fr' ? 'Dépose un podcast ou un long-format. L\'IA fait le reste en moins de 5 minutes.' : 'Drop a podcast or long-form video. The AI does the rest in under 5 minutes.'}
        </p>
        <Link to="/upload">
          <Button className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-12 px-7 mb-8 glow-accent" data-testid="empty-onboarding-cta">
            <UploadIcon className="w-4 h-4 mr-2" />
            {lang === 'fr' ? 'Uploader ma première vidéo' : 'Upload my first video'}
          </Button>
        </Link>
        <div className="grid sm:grid-cols-2 gap-2 text-left max-w-md mx-auto">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
              {s.done ? <CheckCircle2 className="w-4 h-4 text-[#D4FF00] shrink-0" /> : <Circle className="w-4 h-4 text-zinc-600 shrink-0" />}
              <span className={`text-sm ${s.done ? 'text-zinc-300' : 'text-zinc-500'}`}>{lang === 'fr' ? s.fr : s.en}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
